import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_7f244ea6-d37b-42f1-8a6e-12127476b455/artifacts/k63gl0i2_Founder%20life%20creator%20life.png";

const HEADLINE = [
  ["Helping", "Founders"],
  ["Build", "Brands"],
  ["With", "Clarity."],
];

const FLAGS = [
  { flag: "\u{1F1FA}\u{1F1F8}", label: "USA" },
  { flag: "\u{1F1EC}\u{1F1E7}", label: "UK" },
  { flag: "\u{1F1E6}\u{1F1EA}", label: "UAE" },
  { flag: "\u{1F1E6}\u{1F1FA}", label: "Australia" },
];

const lineVariant = {
  hidden: {},
  show: { transition: { staggerChildren: 0.05, delayChildren: 2.05 } },
};
const wordVariant = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export const Hero = ({ onCTA }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-screen w-full overflow-hidden pt-28 md:pt-24"
      data-testid="hero-section"
    >
      {/* soft ambient blobs */}
      <div className="pointer-events-none absolute -top-40 -right-32 h-[520px] w-[520px] rounded-full bg-gold/20 blur-[120px]" />
      <div className="pointer-events-none absolute top-1/3 -left-40 h-[460px] w-[460px] rounded-full bg-burgundy/10 blur-[130px]" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center min-h-[calc(100vh-8rem)]">
        {/* Left */}
        <motion.div style={{ y: textY }} className="lg:col-span-7 flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2, duration: 0.6 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-10 bg-gold" />
            <span className="text-xs md:text-sm tracking-[0.25em] uppercase font-semibold text-gold">
              BRAND STRATEGIST · CREATIVE DIRECTOR
            </span>
          </motion.div>

          <motion.h1
            variants={lineVariant}
            initial="hidden"
            animate="show"
            className="font-heading text-charcoal font-bold tracking-tighter leading-[0.95] text-[3rem] sm:text-6xl lg:text-7xl xl:text-[5.4rem]"
          >
            {HEADLINE.map((line, i) => (
              <span key={i} className="block overflow-hidden py-[0.02em]">
                <motion.span variants={wordVariant} className="inline-block">
                  {line.map((w, j) => (
                    <span key={j} className={w === "Clarity." ? "italic text-burgundy" : ""}>
                      {w}{" "}
                    </span>
                  ))}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.5, duration: 0.8 }}
            className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-charcoal/70"
          >
            From brand strategy and positioning to creative direction and visual systems,
            I help founders turn ideas into clear, distinctive brands built for growth.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.7, duration: 0.8 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button
              onClick={() => window.open("https://calendly.com/ankitagogia/30min", "_blank", "noopener,noreferrer")}
              className="group rounded-full bg-charcoal text-cream hover:bg-burgundy px-8 py-4 text-sm font-medium tracking-wide transition-colors duration-300 flex items-center justify-center gap-2"
              data-testid="hero-cta-audit"
            >
              Book a Brand Position Audit
              <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
            <button
              onClick={() => onCTA("#work")}
              className="rounded-full border border-charcoal/20 bg-transparent text-charcoal hover:border-burgundy hover:text-burgundy px-8 py-4 text-sm font-medium tracking-wide transition-colors duration-300"
              data-testid="hero-cta-work"
            >
              View Case Studies
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 3, duration: 0.8 }}
            className="mt-12"
          >
            <p className="text-xs tracking-[0.2em] uppercase text-charcoal/40 mb-3">
              Trusted by founders & businesses across
            </p>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              {FLAGS.map((f) => (
                <div key={f.label} className="flex items-center gap-2">
                  <span className="text-xl leading-none">{f.flag}</span>
                  <span className="text-sm font-medium text-charcoal/70">{f.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>

        {/* Right — portrait */}
        <div className="lg:col-span-5 relative mb-16 lg:mb-0">
          <motion.div
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 2.1, duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div style={{ y: imgY }} className="relative">
              <div className="relative overflow-hidden rounded-t-[180px] rounded-b-3xl border border-charcoal/5 shadow-[0_40px_80px_-30px_rgba(34,34,34,0.35)]">
                <img
                  src={HERO_IMG}
                  alt="Ankita Gogia, Brand Strategist and Creative Director"
                  fetchPriority="high"
                  decoding="async"
                  className="w-full h-[440px] sm:h-[560px] lg:h-[620px] object-cover object-top"
                  data-testid="hero-portrait"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-cream/30 via-transparent to-transparent" />
              </div>

              {/* floating experience badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 3.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="absolute -left-4 bottom-10 md:-left-10 backdrop-blur-xl bg-cream/80 border border-charcoal/5 rounded-2xl px-6 py-4 shadow-[0_20px_40px_-20px_rgba(34,34,34,0.3)]"
              >
                <p className="font-heading text-4xl text-burgundy leading-none">11+</p>
                <p className="text-xs tracking-wide text-charcoal/60 mt-1">Years of craft</p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3.3, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-charcoal/40"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.div>
      </motion.div>
    </section>
  );
};
